import { FilterType } from 'src/app/components/lib/wizard/filters';

import { createProductFilter } from './filter-helpers';
import { createRequirement, createRequirementFilters } from './requirement-helpers';

import type { ProductResource } from './product-matching';
import type { InitialWizardState } from '../../types';

// Export step constants for type-safe step references
export const WizardSteps = {
  CONFIGURE: 0,
  DETAILS: 1,
  REQUIREMENTS: 2,
  REWARDS: 3,
  ONLINE_DETAILS: 4,
} as const;

export type WizardStepName = keyof typeof WizardSteps;

/**
 * Helper to mark specific steps as visited.
 * Useful when you want to pre-fill steps and show them as completed.
 *
 * @example
 * let state = buildDiscountInitialState({ configure: { discountName: 'Test' } });
 * state = markStepsVisited(state, ['CONFIGURE', 'DETAILS']);
 */
export function markStepsVisited(initialState: InitialWizardState, steps: WizardStepName[]): InitialWizardState {
  const result = { ...initialState };

  steps.forEach((step) => {
    if (step === 'CONFIGURE') {
      result.configureStep = { ...result.configureStep, visited: true };
    } else if (step === 'DETAILS') {
      result.detailsStep = { ...result.detailsStep, visited: true };
    } else if (step === 'REQUIREMENTS') {
      result.requirementsStep = { ...result.requirementsStep, visited: true };
    } else if (step === 'REWARDS') {
      result.rewardsStep = { ...result.rewardsStep, visited: true };
    } else if (step === 'ONLINE_DETAILS') {
      result.onlineDetailsStep = { ...result.onlineDetailsStep, visited: true };
    }
  });

  return result;
}

/**
 * Helper to add products to a specific requirement by index.
 *
 * @example
 * let state = buildDiscountInitialState();
 * state = addProductsToRequirement(state, 1, products);
 */
export function addProductsToRequirement(
  initialState: InitialWizardState,
  requirementIndex: number,
  products: ProductResource[],
  selectionType: FilterType = FilterType.INCLUSIONARY
): InitialWizardState {
  const productFilter = createProductFilter(products, selectionType);

  // Get existing requirement or create a new one with defaults
  const existingRequirement = initialState.requirementsStep?.[requirementIndex];
  const requirement = existingRequirement
    ? createRequirement({
        ...existingRequirement,
        filters: createRequirementFilters({
          ...existingRequirement.filters,
          product: productFilter,
        }),
      })
    : createRequirement({
        filters: createRequirementFilters({
          product: productFilter,
        }),
      });

  return {
    ...initialState,
    requirementsStep: {
      ...initialState.requirementsStep,
      [requirementIndex]: requirement,
    },
  };
}

/**
 * Helper to set the discount name in configure step.
 *
 * @example
 * let state = buildDiscountInitialState();
 * state = withDiscountName(state, 'Black Friday Sale');
 */
export function withDiscountName(initialState: InitialWizardState, name: string): InitialWizardState {
  return {
    ...initialState,
    configureStep: {
      ...initialState.configureStep,
      discountName: name,
    },
  };
}

/**
 * Helper to set start and optionally end dates in details step.
 *
 * @example
 * let state = buildDiscountInitialState();
 * state = withDateRange(state, new Date(), new Date('2024-12-31'));
 */
export function withDateRange(initialState: InitialWizardState, startDate: Date, endDate?: Date): InitialWizardState {
  return {
    ...initialState,
    detailsStep: {
      ...initialState.detailsStep,
      startDate,
      ...(endDate && { endDate, specifyEndDate: true }),
    },
  };
}
